import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { coachQueryOptions } from "~/lib/query-options";
import { getSubcategoryPageContent } from "~/lib/functions/coach-group-seo";
import { getSubcategoryBySlug } from "~/lib/functions/expertise";
import { Heading } from "~/components/ui/heading";
import { CoachCard } from "~/components/coach-card";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BlogGrid } from "~/components/blog-grid";
import { MarkdownContent } from "~/components/markdown-content";
import { FaqList } from "~/components/faq-list";

export const Route = createFileRoute(
  "/_public/coaches/subcategory/$subcategorySlug/",
)({
  loader: async ({ context, params }) => {
    const subcategorySlug = params.subcategorySlug;

    // Pre-load data into React Query and fetch for head function
    const [_coaches, subcategory, page] = await Promise.all([
      context.queryClient.ensureQueryData(
        coachQueryOptions.bySubcategory(subcategorySlug),
      ),
      context.queryClient.ensureQueryData({
        queryKey: ["subcategory", subcategorySlug],
        queryFn: () =>
          getSubcategoryBySlug({ data: { slug: subcategorySlug } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["subcategory", "page", subcategorySlug],
        queryFn: () => getSubcategoryPageContent({ data: { subcategorySlug } }),
      }),
    ]);

    return {
      subcategory,
      page,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          {
            title: `${params.subcategorySlug} Coaches | Dream Coach Match`,
          },
        ],
      };
    }

    const { subcategory } = loaderData;
    const name =
      subcategory?.listingName || subcategory?.name || params.subcategorySlug;

    return {
      meta: [
        {
          title: `${name} Coaches | Dream Coach Match`,
        },
        {
          name: "description",
          content: `Find specialized coaches in ${name} to help you achieve your personal and professional goals.`,
        },
      ],
    };
  },
  component: SubcategoryCoachesPage,
});

function SubcategoryCoachesPage() {
  const { subcategorySlug } = Route.useParams();

  const { data: coaches = [] } = useQuery(
    coachQueryOptions.bySubcategory(subcategorySlug),
  );

  const { data: subcategory } = useQuery({
    queryKey: ["subcategory", subcategorySlug],
    queryFn: () => getSubcategoryBySlug({ data: { slug: subcategorySlug } }),
  });

  const { data: page } = useQuery({
    queryKey: ["subcategory", "page", subcategorySlug],
    queryFn: () => getSubcategoryPageContent({ data: { subcategorySlug } }),
  });

  const name = subcategory?.listingName || subcategory?.name || "";

  const intro1 = `Discover expert coaches specializing in ${name}. Each brings unique skills and perspectives to help you succeed.`;
  const intro2 = `Browse profiles to find a ${name} coach whose approach aligns with your specific needs and goals.`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Heading
        preHeadline="Dream Coach Match"
        headline={name ? `Expert ${name} Coaches` : "Expert Coaches"}
        subheadline={
          name
            ? `Find specialized coaches in ${name} to help you achieve your personal and professional goals.`
            : "Find specialized coaches to help you achieve your personal and professional goals."
        }
        isH1
      />
      <BreakdownIntro
        breakdownName={name} // Will use listingName if available
        breakdownTypeSlug={subcategorySlug}
        introTextLine1={intro1}
        introTextLine2={intro2}
        ctaButtonTextTemplate="Use our Matchmaker to Find Your Perfect {} Coach"
        utmCampaignPrefix="subcategory_coaches"
      />
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {coaches.length > 0 ? (
          coaches.map((coach, i) => (
            <React.Fragment key={coach.id}>
              <CoachCard
                coachData={{
                  coach: {
                    id: coach.id,
                    username: coach.username,
                    fullName: coach.fullName,
                    membershipPlan: coach.membershipPlan,
                    profilePictureUrl: coach.profilePictureUrl,
                    isFeaturedCoach: coach.isFeaturedCoach,
                    isFoundingCoach: coach.isFoundingCoach,
                  },
                  profile: {
                    coachingTitle: coach.coachingTitle,
                    brandLogoUrl: coach.brandLogoUrl,
                    brandName: coach.brandName,
                    title: coach.title,
                  },
                }}
              />
              {i === 11 && (
                <MidListCTA
                  breakdownName={name} // Will use listingName if available
                  breakdownTypeSlug={subcategorySlug}
                  preLinkText="Need help choosing?"
                  ctaLinkTextTemplate="Let us match you with the ideal {} coach!"
                  utmCampaignPrefix="subcategory_coaches_mid"
                />
              )}
            </React.Fragment>
          ))
        ) : (
          <p className="col-span-full text-center text-lg text-gray-500">
            No coaches found in this subcategory yet.
          </p>
        )}
      </div>
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={name} // Will use listingName if available
        breakdownTypeSlug={subcategorySlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate="Ready to connect with a specialized {} coach?"
        mainCtaButtonTextTemplate="Try our free {} Matchmaker tool"
        utmCampaignPrefix="subcategory_coaches_bottom"
      />
      <BlogGrid
        limit={2}
        className="mt-8"
        query={{ "data.tags": { $elemMatch: { $eq: subcategorySlug } } }}
      />
      {page?.contentMd && (
        <div className="prose mx-auto mt-12">
          <MarkdownContent content={page.contentMd} />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
